// Copyright (c) 2022-2024. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import {
    SHELL_FEATURE_LIST_VIEW_CLASS_NAME,
} from "../../../../../core/constants/className";
import { SHELL_FEATURES } from "../../../../../core/constants/shell";
import { useShellFeatureList } from "../../../../hooks/useShellFeatureList";
import { SEODiv } from "../../../common/seo/SEODiv";
import { ProductTableItemModel } from "../../../../../../../io/hyperify/core/store/types/product/ProductTableItemModel";
import {
    T_WEBHOTELS_TITLE,
    T_ORDER_NOW,
    T_SITE_NAME,
    T_READ_MORE,
    T_SHELL_FEATURES_TITLE,
    T_PRODUCT_SHELLS_DESCRIPTION,
    T_SHELL_TITLE,
    T_SHELLS_SEO_IMAGE_EXTRA,
} from "../../../../../core/constants/translation";
import {
    SHELLS_FEATURE_LIST_ROUTE,
} from "../../../../../core/constants/route";
import { ProductPriceType } from "../../../../../../../io/hyperify/core/store/types/product/ProductPriceType";
import { Product } from "../../../../../../../io/hyperify/core/store/types/product/Product";
import { useStoreProductList } from "../../../../../../../io/hyperify/frontend/hooks/useStoreProductList";
import { ProductType } from "../../../../../../../io/hyperify/core/store/types/product/ProductType";
import { ProductUtils } from "../../../../../../../io/hyperify/core/store/utils/ProductUtils";
import { useState } from "react";
import { DEFAULT_SELECTED_PRICE_TYPE_FOR_WEBHOTELS } from "../../../../../core/constants/frontend";
import { CardList } from "../../../common/card/CardList";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { first } from "../../../../../../../io/hyperify/core/functions/first";
import { shuffle } from "../../../../../../../io/hyperify/core/functions/shuffle";
import { SendanorSeoImageData } from "../../../../../core/types/SendanorSeoImageData";
import { getSeoDataFromProductTableItem } from "../utils/getSeoDataFromProductTableItem";
import "./ShellFeatureListView.scss";

export interface ShellFeatureListViewProps {
    readonly t?: TranslationFunction;
    readonly className?: string;
    readonly button?: string;
}

export function ShellFeatureListView (props: ShellFeatureListViewProps) {
    const t = props?.t ?? ((key: string): string => key);
    const className = props?.className;
    const productList: Product[] = useStoreProductList(ProductType.SHELL);
    const [priceType] = useState<ProductPriceType>(DEFAULT_SELECTED_PRICE_TYPE_FOR_WEBHOTELS);

    const productTableItemsList: ProductTableItemModel[] = ProductUtils.createProductTableItemsList(
        T_WEBHOTELS_TITLE,
        "",
        priceType,
        productList,
    );

    // Use one item by random as the seo image featured item
    const [, ...featuredItems] = productTableItemsList;
    const seoImageItem : ProductTableItemModel | undefined = first(shuffle(featuredItems));
    const seoFeaturedItemImageData : SendanorSeoImageData = getSeoDataFromProductTableItem(
        t,
        seoImageItem,
        t(T_PRODUCT_SHELLS_DESCRIPTION),
        t(T_ORDER_NOW),
        t(T_SHELL_TITLE),
        t(T_SHELLS_SEO_IMAGE_EXTRA),
    );

    const featureModelList = useShellFeatureList(SHELL_FEATURES, t);

    return (
        <SEODiv className={ SHELL_FEATURE_LIST_VIEW_CLASS_NAME + (className ? ` ${className}` : "") }
                path={SHELLS_FEATURE_LIST_ROUTE}
                title={seoFeaturedItemImageData.title ?? ''}
                description={seoFeaturedItemImageData.description ?? ''}
                siteName={t(T_SITE_NAME)}
                tagLine={seoFeaturedItemImageData.tagLine}
                price={seoFeaturedItemImageData.price}
                buttonText={seoFeaturedItemImageData.buttonText}
                discountText={seoFeaturedItemImageData.discountText}
                extraInfo={seoFeaturedItemImageData.extraInfo}
        >

            <h3>{t(T_SHELL_FEATURES_TITLE)}</h3>

            <div className={`${SHELL_FEATURE_LIST_VIEW_CLASS_NAME}-extra-products`}>
                <CardList
                    productList={featureModelList}
                    buttonLabel={t(T_READ_MORE)}
                    className={`${SHELL_FEATURE_LIST_VIEW_CLASS_NAME}-white`}
                    t={t}
                />
            </div>

        </SEODiv>
    );
}
